import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { colors } from '@/constants/colors';
import PageTitle from '@/components/Admin/PageTitle';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
   getSmsSettings,
   updateSmsSettings,
   testSms,
   getSmsBalance,
   SmsSettings,
} from '@/api/integration';
import { toast } from 'react-toastify';

const ApiIntegration = () => {
   const queryClient = useQueryClient();

   const { data: settings, isLoading } = useQuery({
      queryKey: ['sms-settings'],
      queryFn: getSmsSettings,
   });

   const { data: balanceData } = useQuery({
      queryKey: ['sms-balance'],
      queryFn: getSmsBalance,
      enabled: !!settings?.apiKey,
      refetchInterval: 60000, // refresh every minute
   });

   const [values, setValues] = useState<SmsSettings>({
      apiKey: '',
      senderName: '',
      enabled: false,
      message: '',
   });
   const [testNumber, setTestNumber] = useState('');
   const [hasChanges, setHasChanges] = useState(false);
   const [senderNameError, setSenderNameError] = useState('');

   useEffect(() => {
      if (settings) {
         setValues(settings);
         setHasChanges(false);
      }
   }, [settings]);

   const handleChange = (
      e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
   ) => {
      const { name, value } = e.target;
      setValues((prev) => ({ ...prev, [name]: value }));
      setHasChanges(true);
      if (name === 'senderName') setSenderNameError('');
   };

   const handleToggle = () => {
      setValues((prev) => ({ ...prev, enabled: !prev.enabled }));
      setHasChanges(true);
   };

   const saveMutation = useMutation({
      mutationFn: () => updateSmsSettings(values),
      onSuccess: (data) => {
         toast.success(data.msg || 'Settings saved');
         queryClient.invalidateQueries({ queryKey: ['sms-settings'] });
         setHasChanges(false);
      },
      onError: (err: any) => {
         const data = err?.response?.data;
         if (data?.field === 'senderName') {
            setSenderNameError(data.err);
            return;
         }
         toast.error(data?.err || 'Failed to save settings');
      },
   });

   const testMutation = useMutation({
      mutationFn: () => testSms(testNumber),
      onSuccess: (data) => {
         toast.success(data.msg || 'Test SMS sent');
         setTestNumber('');
      },
      onError: (err: any) => {
         toast.error(
            err?.response?.data?.err || 'Failed to send test SMS'
         );
      },
   });

   const handleSave = () => {
      if (!values.apiKey.trim()) {
         toast.error('API key is required');
         return;
      }
      saveMutation.mutate();
   };

   const handleTest = () => {
      if (!testNumber.trim()) {
         toast.error('Enter a phone number to test');
         return;
      }
      testMutation.mutate();
   };

   if (isLoading) {
      return (
         <Wrapper>
            <PageTitle title="API & Integration" actions={[]} />
            <LoadingText>Loading settings...</LoadingText>
         </Wrapper>
      );
   }

   return (
      <Wrapper>
         <PageTitle title="API & Integration" actions={[]} />

         <ContentWrapper>
            {/* Semaphore SMS Card */}
            <IntegrationCard>
               <CardHeader>
                  <CardHeaderLeft>
                     <CardLogo>SMS</CardLogo>
                     <CardHeaderInfo>
                        <CardTitle>Semaphore SMS</CardTitle>
                        <CardDesc>
                           Send voucher codes via SMS to your customers after
                           successful payment.
                        </CardDesc>
                     </CardHeaderInfo>
                  </CardHeaderLeft>
                  <CardHeaderRight>
                     {balanceData && (
                        <BalancePill $low={balanceData.balance < 10}>
                           {balanceData.balance} SMS credits
                        </BalancePill>
                     )}
                     <Toggle $active={values.enabled} onClick={handleToggle}>
                        <ToggleKnob $active={values.enabled} />
                     </Toggle>
                  </CardHeaderRight>
               </CardHeader>

               <CardBody>
                  <FormGroup>
                     <Label>API Key</Label>
                     <InputField
                        type="password"
                        name="apiKey"
                        value={values.apiKey}
                        onChange={handleChange}
                        placeholder="Your Semaphore API key"
                     />
                     <HelpText>
                        Get your API key from{' '}
                        <a
                           href="https://semaphore.co"
                           target="_blank"
                           rel="noopener noreferrer"
                        >
                           semaphore.co
                        </a>
                     </HelpText>
                  </FormGroup>

                  <FormGroup>
                     <Label>Sender Name</Label>
                     <InputField
                        type="text"
                        name="senderName"
                        value={values.senderName}
                        onChange={handleChange}
                        placeholder="e.g. SMARTFI"
                        maxLength={11}
                        $error={!!senderNameError}
                     />
                     {senderNameError ? (
                        <FieldError>{senderNameError}</FieldError>
                     ) : (
                        <HelpText>
                           Must be an approved sender name in your Semaphore
                           account. Leave blank to send as "Semaphore".
                        </HelpText>
                     )}
                  </FormGroup>

                  <FormGroup>
                     <Label>Message Template</Label>
                     <TextareaField
                        name="message"
                        value={values.message}
                        onChange={handleChange}
                        placeholder="Your WiFi voucher code is: {code}. Duration: {duration}."
                        rows={3}
                     />
                     <HelpText>
                        Available variables: <Code>{'{code}'}</Code>{' '}
                        <Code>{'{duration}'}</Code>
                     </HelpText>
                  </FormGroup>

                  <MessagePreview>
                     <PreviewLabel>Preview</PreviewLabel>
                     <PreviewText>
                        {(values.message || '')
                           .replace(/{code}/g, 'SMF00123')
                           .replace(/{duration}/g, '3 Hrs')}
                     </PreviewText>
                  </MessagePreview>

                  <BtnRow>
                     <SaveBtn
                        onClick={handleSave}
                        disabled={saveMutation.isPending || !hasChanges}
                     >
                        {saveMutation.isPending ? 'Saving...' : 'Save Settings'}
                     </SaveBtn>
                  </BtnRow>
               </CardBody>

               {/* Test SMS */}
               {values.apiKey && (
                  <TestSection>
                     <TestTitle>Test SMS</TestTitle>
                     <TestRow>
                        <InputField
                           type="tel"
                           value={testNumber}
                           onChange={(e) =>
                              setTestNumber(
                                 e.target.value.replace(/[^0-9]/g, '')
                              )
                           }
                           placeholder="09XXXXXXXXX"
                           maxLength={11}
                           style={{ flex: 1 }}
                        />
                        <TestBtn
                           onClick={handleTest}
                           disabled={testMutation.isPending}
                        >
                           {testMutation.isPending
                              ? 'Sending...'
                              : 'Send Test'}
                        </TestBtn>
                     </TestRow>
                  </TestSection>
               )}
            </IntegrationCard>
         </ContentWrapper>
      </Wrapper>
   );
};

const Wrapper = styled.div`
   max-width: 100%;
   overflow-x: hidden;
`;

const ContentWrapper = styled.div`
   padding: 1rem;
   max-width: 700px;
   @media (max-width: 768px) {
      padding: 12px;
      max-width: 100%;
   }
`;

const IntegrationCard = styled.div`
   border: 1px solid ${colors.GRAY13};
   border-radius: 10px;
   overflow: hidden;
   max-width: 100%;
`;

const CardHeader = styled.div`
   display: flex;
   align-items: center;
   justify-content: space-between;
   gap: 12px;
   padding: 16px 20px;
   background: ${colors.GRAY14};
   border-bottom: 1px solid ${colors.GRAY13};
   flex-wrap: wrap;
   @media (max-width: 768px) {
      padding: 12px 14px;
      gap: 10px;
   }
`;

const CardHeaderLeft = styled.div`
   display: flex;
   align-items: center;
   gap: 14px;
   min-width: 0;
   flex: 1 1 200px;
`;

const CardLogo = styled.div`
   width: 42px;
   height: 42px;
   border-radius: 10px;
   background: ${colors.PRIMARY};
   color: white;
   font-size: 12px;
   font-weight: 700;
   display: flex;
   align-items: center;
   justify-content: center;
`;

const CardHeaderInfo = styled.div`
   min-width: 0;
   flex: 1;
`;

const CardTitle = styled.h3`
   font-size: 15px;
   font-weight: 600;
   color: ${colors.GRAY_DARK};
`;

const CardDesc = styled.p`
   font-size: 13px;
   color: ${colors.GRAY4};
   margin-top: 2px;
   overflow-wrap: anywhere;
   @media (max-width: 768px) {
      font-size: 12px;
      line-height: 1.35;
   }
`;

const CardHeaderRight = styled.div`
   display: flex;
   align-items: center;
   gap: 12px;
`;

const BalancePill = styled.span<{ $low: boolean }>`
   font-size: 12px;
   font-weight: 600;
   padding: 4px 10px;
   border-radius: 20px;
   white-space: nowrap;
   background: ${({ $low }) => ($low ? `${colors.DANGER}15` : `${colors.SUCCESS}15`)};
   color: ${({ $low }) => ($low ? colors.DANGER : colors.SUCCESS)};
   border: 1px solid ${({ $low }) => ($low ? `${colors.DANGER}30` : `${colors.SUCCESS}30`)};
`;

const Toggle = styled.div<{ $active: boolean }>`
   width: 44px;
   height: 24px;
   border-radius: 12px;
   background: ${({ $active }) => ($active ? colors.SUCCESS : colors.GRAY10)};
   cursor: pointer;
   position: relative;
   transition: background 0.2s;
   flex-shrink: 0;
`;

const ToggleKnob = styled.div<{ $active: boolean }>`
   width: 20px;
   height: 20px;
   border-radius: 50%;
   background: var(--app-surface);
   position: absolute;
   top: 2px;
   left: ${({ $active }) => ($active ? '22px' : '2px')};
   transition: left 0.2s;
   box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
`;

const CardBody = styled.div`
   padding: 20px;
   display: flex;
   flex-direction: column;
   gap: 16px;
   @media (max-width: 768px) {
      padding: 14px;
      gap: 14px;
   }
`;

const FormGroup = styled.div``;

const Label = styled.label`
   display: block;
   font-size: 13px;
   font-weight: 600;
   color: ${colors.GRAY1};
   margin-bottom: 6px;
`;

const InputField = styled.input<{ $error?: boolean }>`
   width: 100%;
   max-width: 100%;
   box-sizing: border-box;
   padding: 9px 12px;
   border: 1px solid ${({ $error }) => ($error ? colors.RED : colors.GRAY12)};
   border-radius: 6px;
   font-size: 14px;
   color: ${colors.GRAY_DARK};
   outline: none;

   &:focus {
      border-color: ${colors.PRIMARY};
      box-shadow: 0 0 0 1px ${colors.PRIMARY};
   }

   &::placeholder {
      color: ${colors.GRAY8};
   }
`;

const TextareaField = styled.textarea`
   width: 100%;
   max-width: 100%;
   box-sizing: border-box;
   padding: 9px 12px;
   border: 1px solid ${colors.GRAY12};
   border-radius: 6px;
   font-size: 14px;
   color: ${colors.GRAY_DARK};
   outline: none;
   resize: vertical;
   font-family: inherit;

   &:focus {
      border-color: ${colors.PRIMARY};
      box-shadow: 0 0 0 1px ${colors.PRIMARY};
   }

   &::placeholder {
      color: ${colors.GRAY8};
   }
`;

const FieldError = styled.p`
   font-size: 12px;
   color: ${colors.RED};
   margin-top: 4px;
`;

const HelpText = styled.p`
   font-size: 12px;
   color: ${colors.GRAY5};
   margin-top: 4px;

   a {
      color: ${colors.PRIMARY};
      text-decoration: none;
      &:hover {
         text-decoration: underline;
      }
   }
`;

const Code = styled.code`
   background: ${colors.GRAY14};
   padding: 1px 5px;
   border-radius: 3px;
   font-size: 12px;
   color: ${colors.PRIMARY};
`;

const MessagePreview = styled.div`
   background: ${colors.GRAY14};
   border-radius: 8px;
   padding: 12px 14px;
`;

const PreviewLabel = styled.p`
   font-size: 11px;
   font-weight: 600;
   color: ${colors.GRAY5};
   text-transform: uppercase;
   letter-spacing: 0.3px;
   margin-bottom: 6px;
`;

const PreviewText = styled.p`
   font-size: 13px;
   color: ${colors.GRAY1};
   line-height: 1.5;
`;

const BtnRow = styled.div`
   display: flex;
   justify-content: flex-end;
`;

const SaveBtn = styled.button<{ disabled?: boolean }>`
   background: ${({ disabled }) => (disabled ? colors.GRAY10 : colors.PRIMARY)};
   color: ${({ disabled }) => (disabled ? colors.GRAY5 : 'white')};
   border: none;
   border-radius: 6px;
   padding: 9px 20px;
   font-size: 14px;
   font-weight: 600;
   cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};

   &:hover:not(:disabled) {
      opacity: 0.9;
   }
`;

const TestSection = styled.div`
   padding: 16px 20px;
   border-top: 1px solid ${colors.GRAY13};
   background: ${colors.GRAY14};
   @media (max-width: 768px) {
      padding: 12px 14px;
   }
`;

const CardHeaderRightWrap = styled.div``;

const TestTitle = styled.p`
   font-size: 13px;
   font-weight: 600;
   color: ${colors.GRAY1};
   margin-bottom: 8px;
`;

const TestRow = styled.div`
   display: flex;
   gap: 8px;
`;

const TestBtn = styled.button<{ disabled?: boolean }>`
   background: var(--app-surface);
   color: ${colors.PRIMARY};
   border: 1px solid ${colors.PRIMARY};
   border-radius: 6px;
   padding: 9px 16px;
   font-size: 13px;
   font-weight: 600;
   cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
   white-space: nowrap;
   opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};

   &:hover:not(:disabled) {
      background: ${colors.PRIMARY};
      color: white;
   }
`;

const LoadingText = styled.div`
   text-align: center;
   padding: 2rem;
   color: ${colors.GRAY5};
`;

export default ApiIntegration;
